import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Calendar, Activity, Edit, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getLocalTodayISO } from '../utils/dateUtils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const StepByStepNutritionEntry = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    date: getLocalTodayISO(),
    vitamin_d: '',
    b12: '',
    folate: '',
    ferritin: ''
  });

  // Nutrition marker definitions in sequential order
  const getMarkerConfig = (markerId) => {
    const isUS = user?.country === 'US';
    
    const configs = {
      vitamin_d: {
        id: 'vitamin_d',
        label: 'Vitamin D',
        displayUnit: isUS ? 'ng/mL' : 'nmol/L',
        canonicalUnit: 'nmol/L',
        range: isUS ? '20-50' : '50-125',
        toCanonical: (value) => isUS ? value * 2.5 : value,  // ng/mL to nmol/L
        fromCanonical: (value) => isUS ? value / 2.5 : value
      },
      b12: {
        id: 'b12',
        label: 'Vitamin B12',
        displayUnit: isUS ? 'pg/mL' : 'pmol/L',
        canonicalUnit: 'pmol/L',
        range: isUS ? '200-900' : '150-700',
        toCanonical: (value) => isUS ? value * 0.738 : value,  // pg/mL to pmol/L
        fromCanonical: (value) => isUS ? value / 0.738 : value
      },
      folate: {
        id: 'folate',
        label: 'Folate',
        displayUnit: isUS ? 'ng/mL' : 'nmol/L',
        canonicalUnit: 'nmol/L',
        range: isUS ? '3-20' : '7-45',
        toCanonical: (value) => isUS ? value * 2.266 : value,  // ng/mL to nmol/L
        fromCanonical: (value) => isUS ? value / 2.266 : value
      },
      ferritin: {
        id: 'ferritin',
        label: 'Ferritin',
        displayUnit: 'ng/mL',  // Same for both countries
        canonicalUnit: 'μg/L',
        range: '30-300',
        toCanonical: (value) => value,  // ng/mL = μg/L
        fromCanonical: (value) => value
      }
    };
    
    return configs[markerId];
  };

  // Sequential markers (all required in flow, but values optional)
  const markers = [
    {
      step: 2,
      field: 'vitamin_d',
      config: getMarkerConfig('vitamin_d')
    },
    {
      step: 3,
      field: 'b12',
      config: getMarkerConfig('b12')
    },
    {
      step: 4,
      field: 'folate',
      config: getMarkerConfig('folate')
    },
    {
      step: 5,
      field: 'ferritin',
      config: getMarkerConfig('ferritin')
    }
  ];

  const totalSteps = markers.length + 2; // Date + 4 markers + review

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      // Build payload with canonical units
      const payload = { date: formData.date, units: 'canonical' };
      
      // Add only markers that have values
      markers.forEach(({ field }) => {
        if (formData[field] !== '') {
          const config = getMarkerConfig(field);
          const displayValue = parseFloat(formData[field]);
          const canonicalValue = config.toCanonical(displayValue);
          payload[field] = parseFloat(canonicalValue.toFixed(2));
        }
      });

      // Validate at least one marker is entered
      if (Object.keys(payload).length === 2) {
        toast.error('Please enter at least one nutrition marker value');
        return;
      }

      await axios.post(`${API_URL}/api/nutrition`, payload, {
        headers: getAuthHeader()
      });

      toast.success('Nutrition record saved successfully');
      navigate('/nutrition');
    } catch (error) {
      toast.error('Failed to save nutrition record');
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    // Navigate back without confirmation for better mobile UX
    navigate('/nutrition');
  };

  const getCurrentMarker = () => {
    return markers.find(m => m.step === currentStep);
  };

  const currentMarker = getCurrentMarker();
  const currentMarkerConfig = currentMarker ? currentMarker.config : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      <div className="max-w-md mx-auto px-4 py-6 w-full box-border">
        {/* Progress Indicator */}
        {currentStep <= totalSteps && (
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-sm text-slate-600">
                Step {currentStep} of {totalSteps}
              </span>
            </div>
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-all ${
                    i + 1 <= currentStep ? 'bg-emerald-500' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Test Date */}
        {currentStep === 1 && (
          <div className="space-y-6 w-full">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-3">
                <Calendar className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Nutrition Test Date</h1>
              <p className="text-sm text-slate-600">When were these results taken?</p>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-slate-200 w-full overflow-hidden">
              <div className="w-full">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Test Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  max={getLocalTodayISO()}
                  className="w-full text-lg font-semibold text-slate-900 bg-slate-50 rounded-xl px-3 py-3 border-2 border-slate-200 focus:border-emerald-500 outline-none transition-colors text-center box-border"
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-2">
                  Make sure this matches the date on your pathology report.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={handleCancel}
                className="flex-1 min-w-0 text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 min-w-0 bg-emerald-600 hover:bg-emerald-700 text-sm"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Steps 2-5: Marker Entry */}
        {currentStep >= 2 && currentStep <= 5 && currentMarkerConfig && (
          <div className="space-y-6 w-full">
            {/* Test Date Display */}
            <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200">
              <div className="flex items-center justify-center gap-2 text-emerald-700">
                <Calendar className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium">
                  Test Date: {new Date(formData.date).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-3">
                <Activity className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">{currentMarkerConfig.label}</h1>
              <p className="text-sm text-slate-600">Enter the value from your test results</p>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-slate-200 w-full overflow-hidden">
              <div className="w-full">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Value (optional)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData[currentMarker.field]}
                  onChange={(e) => setFormData({ ...formData, [currentMarker.field]: e.target.value })}
                  className="w-full text-2xl font-semibold text-slate-900 bg-slate-50 rounded-xl px-3 py-3 border-2 border-slate-200 focus:border-emerald-500 outline-none transition-colors text-center box-border"
                  placeholder="0.0"
                  autoFocus
                />
                <p className="text-sm text-slate-500 mt-2 text-center">
                  {currentMarkerConfig.displayUnit} • Range: {currentMarkerConfig.range}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={handleBack}
                className="flex-1 min-w-0 text-sm px-2"
              >
                <ArrowLeft className="h-4 w-4 mr-1 flex-shrink-0" />
                Back
              </Button>
              <Button
                variant="outline"
                onClick={handleNext}
                className="flex-1 min-w-0 text-sm px-2"
              >
                Skip
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 min-w-0 bg-emerald-600 hover:bg-emerald-700 text-sm px-2"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-1 flex-shrink-0" />
              </Button>
            </div>
          </div>
        )}

        {/* Final Step: Review & Submit */}
        {currentStep === totalSteps && (
          <div className="fixed inset-0 bg-gradient-to-b from-slate-50 to-white flex flex-col">
            {/* Fixed Header with Exit */}
            <div className="flex-shrink-0 pt-6 px-4 pb-4">
              <div className="flex justify-end max-w-md mx-auto mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/nutrition')}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Exit
                </Button>
              </div>
              <div className="text-center mb-4">
                <p className="text-sm text-slate-500 mb-2">Step {currentStep} of {totalSteps}</p>
                <h1 className="text-xl font-bold text-slate-900">Review Nutrition Record</h1>
                <p className="text-sm text-slate-600">Check all values before submitting</p>
              </div>

              {/* Test Date */}
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200 max-w-md mx-auto">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-emerald-600" />
                  <div>
                    <p className="text-xs text-slate-600">Test Date</p>
                    <p className="text-base font-bold text-slate-900">
                      {new Date(formData.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable Markers - Show ALL markers like Blood */}
            <div className="flex-1 overflow-y-auto px-4">
              <div className="bg-white rounded-2xl p-3 border border-slate-200 space-y-2 max-w-md mx-auto">
                {markers.map((marker) => {
                  const hasValue = formData[marker.field] !== '';
                  return (
                    <div
                      key={marker.field}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        hasValue ? 'bg-slate-50' : 'bg-slate-50/50'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-600">{marker.config.label}</p>
                        {hasValue ? (
                          <p className="text-base font-bold text-slate-900">
                            {formData[marker.field]} <span className="text-xs text-slate-500">{marker.config.displayUnit}</span>
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400">Not entered</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentStep(marker.step)}
                        className="text-emerald-600 hover:text-emerald-700"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    </div>
                  );
                })}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mt-3 max-w-md mx-auto">
                <p className="text-xs text-blue-800">
                  This information is for tracking purposes only and is not medical advice.
                </p>
              </div>
            </div>

            {/* Fixed Footer Buttons */}
            <div className="flex-shrink-0 p-4 pb-24 bg-white border-t border-slate-200">
              <div className="flex gap-3 max-w-md mx-auto">
                <Button variant="ghost" onClick={handleBack} className="flex-1 min-w-0">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <Button onClick={handleSubmit} className="flex-1 min-w-0 bg-emerald-600 hover:bg-emerald-700">
                  <Check className="h-4 w-4 mr-1" />
                  Submit
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepByStepNutritionEntry;
